import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStaffMe } from '../../hooks/useStaffMe';
import { uatAssessUrl, BriefAlreadyExistsError } from '../../lib/partner-briefs-api';
import { ActionConfirmModal } from './ActionConfirmModal';
import { useToast } from '../../components/ToastProvider';
import { ResidencyCountryField, resolveCreateZone } from '../../components/ResidencyCountryField';
import { useZoneMap } from '../../lib/zone-map';
import { zoneFor } from '../../obw/residency';
import { useWorkingZone } from '../../lib/zones';

/**
 * Manual Add Partner panel — admin-initiated partner-onboarding path
 * (ADR-032, Phase 5d-1 follow-up).
 *
 * BDM enters URL + Business Name + Email. The POW Suitability Agent runs
 * as today. BDM manually approves; on approve the system sends an OBW
 * invitation email (admin_invited token, 3-day TTL) so the applicant can
 * complete account creation without a prior subscription.
 *
 * Super_admin retains a "UAT diagnostic" toggle inside the panel — when
 * checked, the submission is tagged briefSource=uat (sentinel CN
 * NX-UATEST, email field optional). nexa_admin sees the production-mode
 * panel only.
 *
 * Backend integration (manualAddPartner Lambda + briefSource field +
 * invitation email pipeline) lands in a follow-up commit per ADR-032
 * §"Implementation plan". This version stores the email on the Brief
 * via the existing uatAssessUrl handler extended with an email param;
 * the manual_add → manual_add_invited transition + email send wire up
 * after the operator prereqs (M365 alias + secret) are in place.
 */
// OBW email validation regex — reused from nexa-demo/nexanomics-obw.js
// line 1313 (basic RFC-like pattern, validates `local@domain.tld` shape).
const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function UatAssessField() {
  const navigate = useNavigate();
  const me = useStaffMe();
  const toast = useToast();
  const [url, setUrl] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [applicantEmail, setApplicantEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  // UAT mode toggle — super_admin only. When checked, behaves as the
  // legacy diagnostic path (CN NX-UATEST, email field optional). When
  // unchecked (default for nexa_admin; default-checked for super_admin
  // during the build-out phase so the UAT path stays usable), it's a
  // production Manual Add Partner submission.
  const isSuperAdmin = me.status === 'loaded' && me.me?.role === 'super_admin';
  const [uatMode, setUatMode] = useState(isSuperAdmin);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<{ applicationId: string; existingStatus: string; submittedUrl: string; zone: string } | null>(null);
  /**
   * ⭐ ADR-124 D5 — THE PARTNER'S COUNTRY PUTS THE BRIEF IN ITS ZONE. ADR-122 finding #4 gave this
   * form a free "which zone" picker; the zone now FOLLOWS from the country the partner business is
   * registered in, through the served map — a UK partner's Brief is created in the US zone,
   * whichever zone the BDM is working in.
   */
  const [country, setCountry] = useState('');
  const zoneMap = useZoneMap();
  const workingZone = useWorkingZone();
  const derived = country && zoneMap ? zoneFor(zoneMap, country) : null;
  const createZone = resolveCreateZone(derived?.zone);

  if (me.status !== 'loaded') return null;
  const role = me.me?.role;
  // Visible to super_admin (with UAT toggle) and nexa_admin (production
  // Manual Add Partner only). All other roles see nothing.
  if (role !== 'super_admin' && role !== 'nexa_admin') return null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setEmailError(null);
    const raw = url.trim();
    if (!raw) {
      setError('URL is required.');
      return;
    }
    // Accept bare domains — auto-prepend https:// so operators can paste
    // "redmoth.com.au" instead of needing to remember the protocol.
    const normalised = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    try {
      new URL(normalised);
    } catch {
      setError('That doesn’t look like a valid URL. Try `example.com` or `https://example.com`.');
      return;
    }
    // Email validation — required in Manual Add Partner mode (production);
    // optional in UAT diagnostic mode. Uses the OBW regex per
    // admin-portal-ui-standards.md and ADR-032 §10.
    const emailTrim = applicantEmail.trim();
    if (!uatMode) {
      if (!emailTrim) {
        setEmailError('Email is required for a Manual Add Partner submission.');
        return;
      }
      if (!EMAIL_RX.test(emailTrim)) {
        setEmailError('That email doesn’t look right. Format: name@example.com');
        return;
      }
    } else if (emailTrim && !EMAIL_RX.test(emailTrim)) {
      // UAT mode: email optional, but if entered must be valid
      setEmailError('That email doesn’t look right. Format: name@example.com');
      return;
    }
    if (!createZone) {
      setError('Choose the country the partner business is registered in.');
      return;
    }
    setLoading(true);
    try {
      const result = await uatAssessUrl(
        normalised,
        businessName.trim() || undefined,
        emailTrim || undefined,
        createZone,
        country,
      );
      // ⚠ THE ZONE RIDES THE NAVIGATION, AND IT MUST. `BriefDetailPage` resolves a brief in the
      // zone its URL names and falls back to HOME when the URL names none — so a Brief just
      // created in us-east-1, linked without `?zone=`, would be looked up in ap-southeast-2 and
      // reported as not found seconds after being created. This is ADR-122 Pattern 2's rule
      // applied at the moment a record is born, which is the one moment its zone is known for
      // certain and has not yet been written down anywhere.
      navigate(
        `/admin/partners/${encodeURIComponent(result.applicationId)}` +
          // Always carries the zone (ADR-123 Stage 3b) — a detail link without one is the
          // "asks the wrong zone, looks like it doesn't exist" class ADR-122 Pattern 2 closed.
          `?zone=${encodeURIComponent(createZone.code)}`,
      );
    } catch (err) {
      if (err instanceof BriefAlreadyExistsError) {
        // Open the styled in-app modal asking whether to navigate to the
        // existing Brief or keep the form. Browser-native confirm() is
        // jarring next to the Nexanomics dark UI. Pass the URL the
        // operator just typed so the modal can echo it back — important
        // when the form has been populated for a while and the operator
        // may have forgotten what they entered.
        setConflict({
          applicationId: err.applicationId,
          existingStatus: err.existingStatus,
          submittedUrl: normalised,
          zone: createZone.code,
        });
      } else {
        setError(err instanceof Error ? err.message : 'Could not start UAT run.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="portal-uat-field" aria-label={uatMode ? 'UAT assessment trigger' : 'Manual Add Partner'}>
      <div className="portal-uat-field-header">
        <h2>{uatMode ? 'UAT assessment (super_admin diagnostic)' : 'Manual Add Partner'}</h2>
      </div>
      <p className="portal-uat-field-help">
        {uatMode
          ? <>Diagnostic run — creates a synthetic Brief tagged <code>NX-UATEST</code>. <strong>Super_admin only.</strong></>
          : <>BDM-initiated partner onboarding (ADR-032). The agent runs Stage 1 + Stage 2; you approve manually, then the system emails the applicant a sign-up link.</>
        }
      </p>
      {isSuperAdmin && (
        <label className="portal-uat-mode-toggle">
          <input
            type="checkbox"
            checked={uatMode}
            onChange={(e) => setUatMode(e.target.checked)}
            disabled={loading}
          />
          <span>UAT diagnostic mode <em>(super_admin)</em> — bypass production rubric + skip email requirement</span>
        </label>
      )}
      <form onSubmit={(e) => void submit(e)} className="portal-uat-field-form">
        <label className="portal-uat-field-label">
          <span>URL</span>
          <input
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="example.com.au or https://example.com.au"
            disabled={loading}
            required
            autoComplete="off"
            spellCheck={false}
          />
        </label>
        <label className="portal-uat-field-label">
          <span>
            Business name {uatMode
              ? <em className="portal-uat-field-hint">(auto-filled from page title in UAT mode)</em>
              : ''}
          </span>
          <input
            type="text"
            value={uatMode ? '' : businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            placeholder={uatMode ? 'Auto-filled from page title — typed values are ignored' : 'Example Pty Ltd'}
            maxLength={200}
            disabled={loading || uatMode}
            required={!uatMode}
          />
        </label>
        <label className="portal-uat-field-label">
          <span>Applicant email {uatMode ? '(optional)' : ''}</span>
          <input
            type="email"
            value={applicantEmail}
            onChange={(e) => { setApplicantEmail(e.target.value); setEmailError(null); }}
            onBlur={() => {
              const t = applicantEmail.trim();
              if (t && !EMAIL_RX.test(t)) {
                setEmailError('That email doesn’t look right. Format: name@example.com');
              }
            }}
            placeholder="contact@example.com"
            maxLength={254}
            disabled={loading}
            required={!uatMode}
            autoComplete="email"
          />
          {emailError && <span className="portal-uat-field-inline-error">{emailError}</span>}
        </label>
        {/* ADR-124 D5 — the partner's country; the Brief's zone follows from it. */}
        <ResidencyCountryField
          id="manual-add-partner-country"
          value={country}
          onChange={setCountry}
          map={zoneMap}
          workingZone={workingZone}
          disabled={loading}
        />
        <div className="portal-uat-field-actions">
          <button type="submit" className="portal-btn" disabled={loading || !url.trim() || !createZone || (!uatMode && (!businessName.trim() || !applicantEmail.trim()))}>
            {loading ? 'Starting…' : uatMode ? 'Submit for assessment' : 'Add Partner'}
          </button>
          <span className="portal-uat-field-cost">
            Est. <strong>~$0.30–$0.50</strong> per run · hard cap <strong>$2</strong>
            {' '}per ADR-027 §10.5.
          </span>
          {/* UAT helper — fires sample toasts of each variant so the
             operator can verify the toast UX without needing to trigger
             a real API error. Staggered by 400ms so the stack animation
             is visible. super_admin only (parent gate). */}
          <button
            type="button"
            className="portal-link-button-secondary portal-uat-toast-test"
            onClick={() => {
              toast.push({
                variant: 'info',
                title: 'Test toast — info',
                detail: 'Background context. Auto-dismisses after 6s.',
              });
              setTimeout(() => toast.push({
                variant: 'success',
                title: 'Test toast — success',
                detail: 'Action completed. Auto-dismisses after 4s.',
              }), 400);
              setTimeout(() => toast.push({
                variant: 'error',
                title: 'Test toast — error (HTTP 503)',
                detail: '/admin/example/path\n{"message":"Service Unavailable"}',
              }), 800);
            }}
            title="Fire one of each toast variant — confirms the notification UX"
          >
            🧪 Test toasts
          </button>
        </div>
        {error && (
          <div className="portal-error" role="alert">{error}</div>
        )}
      </form>
      <ActionConfirmModal
        open={conflict !== null}
        title="Brief already exists"
        intent="warning"
        primaryLabel="Open existing Brief"
        onConfirm={() => {
          if (conflict) {
            const id = conflict.applicationId;
            setConflict(null);
            // The duplicate was detected BY THE ZONE WE SUBMITTED TO, so that is the zone it
            // lives in — carry it, for the same reason the success path does.
            navigate(
              `/admin/partners/${encodeURIComponent(id)}` +
                `?zone=${encodeURIComponent(conflict.zone)}`,
            );
          }
        }}
        onCancel={() => setConflict(null)}
      >
        <p>
          A Brief for <code>{conflict?.submittedUrl ?? ''}</code> already exists in status{' '}
          <code>{conflict?.existingStatus ?? ''}</code>.
        </p>
        <p>
          Choose <strong>Open existing Brief</strong> to jump straight to it, or{' '}
          <strong>Cancel</strong> to keep your form so you can edit the URL or soft-delete
          the existing Brief first.
        </p>
        {conflict?.existingStatus && /^(rejected|approved|active|suspended|agent_failed)/.test(conflict.existingStatus) && (
          <p className="portal-uat-conflict-hint">
            Tip: closed Briefs (rejected / approved / etc.) are hidden from the queue by default.
            Toggle <strong>Show closed</strong> below the Applications header to see them.
          </p>
        )}
      </ActionConfirmModal>
    </section>
  );
}
